export const BLOG_ARTICLES = [
  {
    id: 1,
    title: "How to Choose the Right Electric Golf Scooter",
    slug: "how-to-choose-electric-golf-scooter",
    excerpt: "Discover the key factors to consider when selecting your perfect golf mobility solution—from motor power to battery life and terrain compatibility.",
    image: "images/gallery/gallery-01.jpg",
    category: "Buying Guide",
    readTime: "5 min read",
    date: "January 15, 2026",
    content: `
      <h3>Understanding Your Needs</h3>
      <p>Choosing the right electric golf scooter starts with understanding your specific requirements. Consider the following factors:</p>
      
      <h4>1. Course Terrain</h4>
      <p>Hilly courses require more motor power and torque. Our M12-1 and ESGOLF models excel on challenging terrain with their powerful motors and climb angles up to 20°.</p>
      
      <h4>2. Battery Life Requirements</h4>
      <p>How many holes do you typically play? If you're a regular 36-hole player, look for models with 20Ah+ batteries. The M12-3 offers exceptional range for extended rounds.</p>
      
      <h4>3. Weight Capacity</h4>
      <p>Ensure the scooter can comfortably support your weight plus any accessories. All our models support 110-160kg depending on configuration.</p>
      
      <h4>4. Storage & Portability</h4>
      <p>Need to transport it in your car? All Lectric Leisure scooters feature one-second quick-fold designs for easy storage and transport.</p>
      
      <h4>5. Accessories</h4>
      <p>Consider what extras you'll need—seats for comfort, umbrellas for sun protection, or coolers for refreshments. Our models support a full range of professional accessories.</p>
      
      <hr />

<h3>Model Comparison</h3>
<table>
  <thead>
    <tr>
      <th>Model</th>
      <th>Best For</th>
      <th>Main Strength</th>
      <th>Battery Range</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td><strong>M12-1</strong></td>
      <td>Comfort-focused golfers</td>
      <td>Stability & accessory support</td>
      <td>Long 18-hole rounds</td>
    </tr>
    <tr>
      <td><strong>M12-3</strong></td>
      <td>Power users</td>
      <td>High torque & hill climbing</td>
      <td>36 holes+</td>
    </tr>
    <tr>
      <td><strong>ESGOLF</strong></td>
      <td>Travel golfers</td>
      <td>Lightweight & portable</td>
      <td>Swappable battery</td>
    </tr>
  </tbody>
</table>

    `
  },
  {
    id: 2,
    title: "Essential Maintenance Tips for Electric Golf Scooters",
    slug: "electric-golf-scooter-maintenance-tips",
    excerpt: "Keep your scooter running smoothly with these expert maintenance tips. Simple steps to extend battery life and ensure peak performance.",
    image: "images/gallery/gallery-04.jpg",
    category: "Maintenance",
    readTime: "7 min read",
    date: "January 10, 2026",
    content: `
      <h3>Monthly Maintenance Checklist</h3>
      
      <hr />

<h3>Monthly Maintenance Overview</h3>
<table>
  <thead>
    <tr>
      <th>Component</th>
      <th>Check Frequency</th>
      <th>What to Look For</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>Battery</td>
      <td>After every round</td>
      <td>Proper charge level</td>
    </tr>
    <tr>
      <td>Tires</td>
      <td>Monthly</td>
      <td>Pressure & tread wear</td>
    </tr>
    <tr>
      <td>Brakes</td>
      <td>Monthly</td>
      <td>Responsiveness</td>
    </tr>
    <tr>
      <td>Bolts</td>
      <td>Monthly</td>
      <td>Loose fittings</td>
    </tr>
  </tbody>
</table>

<h4>Battery Care</h4>
<ul>
  <li><strong>Charge Regularly:</strong> Avoid full discharge. Charge after every round.</li>
  <li><strong>Storage:</strong> Keep at 50–70% charge during long storage.</li>
  <li><strong>Temperature:</strong> Never charge in freezing or extreme heat.</li>
</ul>

      
      <h4>Tire Maintenance</h4>
      <p><strong>Check Pressure:</strong> Monthly tire pressure checks ensure optimal performance and comfort. Recommended: 35-40 PSI.</p>
      <p><strong>Inspect Tread:</strong> Look for wear, cracks, or embedded objects. Replace if tread is worn.</p>
      
      <h4>Brake System</h4>
      <p><strong>Test Regularly:</strong> Ensure both mechanical and electronic brakes respond properly.</p>
      <p><strong>Adjustment:</strong> If brakes feel loose or unresponsive, contact our service team for adjustment.</p>
      
      <h4>Cleaning</h4>
      <p><strong>After Each Round:</strong> Wipe down the scooter with a damp cloth to remove grass, dirt, and moisture.</p>
      <p><strong>Deep Clean:</strong> Monthly detailed cleaning of motor housing, deck, and electrical connections.</p>
      <p><strong>Avoid:</strong> Never use high-pressure water directly on electrical components.</p>
      
      <h4>Bolts & Connections</h4>
      <p><strong>Tighten:</strong> Check and tighten handlebar, stem, and deck bolts monthly.</p>
      <p><strong>Inspect:</strong> Look for loose wiring or damaged cables.</p>
      
      <h3>Seasonal Maintenance</h3>
      <p><strong>Spring:</strong> Full service check, battery health test, tire replacement if needed.</p>
      <p><strong>Summer:</strong> Extra attention to battery cooling, check for heat-related issues.</p>
      <p><strong>Fall:</strong> Prepare for wet conditions, check brake pads and tire tread.</p>
      <p><strong>Winter:</strong> Reduce battery stress, store properly if not in use.</p>
    `
  },
  {
    id: 3,
    title: "Electric vs. Gas Golf Carts: The Complete Comparison",
    slug: "electric-scooter-vs-gas-golf-cart",
    excerpt: "Why modern golfers are switching from traditional gas carts to nimble electric scooters. Compare costs, performance, and environmental impact.",
    image: "images/gallery/gallery-06.jpg",
    category: "Comparison",
    readTime: "6 min read",
    date: "December 28, 2025",
    content: `
      <h3>The New Era of Golf Mobility</h3>
      
      <h4>Cost Comparison</h4>
      <p><strong>Initial Investment:</strong></p>
      <ul>
        <li>Gas Golf Cart: $8,000 - $15,000</li>
        <li>Electric Golf Scooter: Contact for pricing (significantly lower)</li>
      </ul>
      
      <p><strong>Operating Costs (Annual):</strong></p>
      <ul>
        <li>Gas Cart: $500-800 (fuel, oil, maintenance)</li>
        <li>Electric Scooter: $50-100 (electricity only)</li>
      </ul>
      
      <h4>Performance</h4>
      <p><strong>Speed:</strong> Both reach 25 km/h, but scooters offer better acceleration and maneuverability.</p>
      <p><strong>Range:</strong> Gas carts win for all-day commercial use, but scooters excel for personal 18-36 hole rounds.</p>
      <p><strong>Terrain:</strong> Modern electric scooters like our M12-1 handle hills up to 20° with ease.</p>
      
      <h4>Environmental Impact</h4>
      <p><strong>Emissions:</strong> Electric scooters produce zero emissions during operation.</p>
      <p><strong>Noise:</strong> Whisper-quiet motors vs. loud gas engines.</p>
      <p><strong>Turf Protection:</strong> Lighter weight and smaller footprint reduce course damage.</p>
      
      <h4>Convenience</h4>
      <p><strong>Storage:</strong> Scooters fold in one second and fit in car trunks. Gas carts require dedicated storage.</p>
      <p><strong>Maintenance:</strong> Scooters need minimal upkeep. Gas carts require regular oil changes, filter replacements, etc.</p>
      <p><strong>Charging:</strong> Plug in overnight at home vs. refueling trips.</p>
      
      <h3>The Verdict</h3>
      <p>For personal golfers, electric scooters offer superior value, convenience, and environmental benefits. Gas carts still dominate commercial fleet operations, but that's changing fast.</p>
    `
  }
];